const ROLE_CHIP = {
  admin:        'bg-pink-50 text-pink-800 ring-pink-200',
  instructor:   'bg-blue-50 text-blue-800 ring-blue-200',
  demonstrator: 'bg-indigo-50 text-indigo-800 ring-indigo-200',
  manager:      'bg-amber-50 text-amber-800 ring-amber-200',
  student:      'bg-emerald-50 text-emerald-800 ring-emerald-200',
};

const STATUS_CHIP = {
  active:   'bg-emerald-50 text-emerald-800 ring-emerald-200',
  inactive: 'bg-red-50 text-red-700 ring-red-200',
  pending:  'bg-amber-50 text-amber-800 ring-amber-200',
};

const Chip = ({ label, colorClass }) => (
  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ring-1 ${colorClass}`}>
    {label}
  </span>
);

const UserList = ({
  users,
  onEdit,
  onToggleStatus,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
}) => (
  <section className="rounded-lg border border-stone-200 bg-white">
    <div className="border-b border-stone-200 px-3 py-2">
      <h2 className="text-base font-semibold tracking-tight text-stone-950">User roster</h2>
      <p className="text-xs text-stone-500">
        Showing {users.length ? (currentPage - 1) * pageSize + 1 : 0}–
        {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
      </p>
    </div>

    {!users.length ? (
      <div className="px-3 py-10 text-center text-sm text-stone-500">No users match the selected filters.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              {['Name', 'Email', 'Role', 'Status', ''].map((col) => (
                <th
                  key={col}
                  className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={`${user.id}-${user.role}`} className="group">
                <td className="border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                  <p className="font-semibold text-stone-950">
                    {user.firstName} {user.lastName}
                  </p>
                </td>
                <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2.5 text-stone-700 group-hover:bg-stone-50">
                  {user.email}
                </td>
                <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                  <Chip
                    label={user.role}
                    colorClass={ROLE_CHIP[user.role] || 'bg-stone-100 text-stone-700 ring-stone-200'}
                  />
                </td>
                <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2.5 group-hover:bg-stone-50">
                  <Chip
                    label={user.status || 'active'}
                    colorClass={STATUS_CHIP[user.status] || 'bg-stone-100 text-stone-700 ring-stone-200'}
                  />
                </td>
                <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2.5 text-right group-hover:bg-stone-50">
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => onEdit(user)}
                      className="text-xs font-semibold text-[#b91c66] hover:text-[#9d174d]"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleStatus(user)}
                      className="text-xs font-semibold text-stone-500 hover:text-stone-900"
                    >
                      {user.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Pagination footer */}
    {totalPages > 1 && (
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 px-3 py-2">
        <p className="text-xs text-stone-500">
          Page {currentPage} of {totalPages} · {totalCount} users
        </p>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-40"
          >
            Previous
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
            const p = start + i;
            return (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={`rounded border px-2.5 py-1 text-xs font-semibold transition ${
                  p === currentPage
                    ? 'border-stone-950 bg-stone-950 text-white'
                    : 'border-stone-200 text-stone-700 hover:bg-stone-50'
                }`}
              >
                {p}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="rounded border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    )}
  </section>
);

export default UserList;
